import React, { useEffect, useState } from "react";
import { Lock, Unlock, Download } from "lucide-react";
import { toast } from "sonner";
import api, { today, formatApiError } from "../lib/api";

const ctl = "h-9 px-2 rounded-md border border-input bg-card text-sm num focus:outline-none focus:ring-2 focus:ring-ring/40";
const fmt = (iso) => (iso ? new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }) : "");
const monthAgo = () => { const d = new Date(); d.setDate(d.getDate() - 30); return d.toISOString().slice(0, 10); };

const exportCsv = (d) => {
  const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const lines = [["Cash book date", "Action", "By", "When", "Details"].join(","),
    ...d.history.map((h) => [h.date, h.action === "lock" ? "Locked" : "Unlocked", h.by, fmt(h.at), h.details].map(esc).join(","))];
  const a = document.createElement("a");
  a.href = URL.createObjectURL(new Blob(["\ufeff" + lines.join("\r\n")], { type: "text/csv;charset=utf-8" }));
  a.download = `cashbook_locks_${d.start}_${d.end}.csv`; a.click(); URL.revokeObjectURL(a.href);
};

const HistoryRow = ({ h, i }) => (
  <div data-testid={`lock-hist-${i}`} className="grid grid-cols-2 md:grid-cols-[110px_100px_1fr_170px] gap-2 py-2 text-sm items-center">
    <span className="num font-semibold">{h.date}</span>
    <span className={`flex items-center gap-1 text-xs font-bold ${h.action === "lock" ? "text-destructive" : "text-[hsl(var(--positive))]"}`}>
      {h.action === "lock" ? <Lock size={12} /> : <Unlock size={12} />}{h.action === "lock" ? "LOCKED" : "UNLOCKED"}</span>
    <span className="flex-1 min-w-0">{h.by}{h.details && /up to/.test(h.details) ? <span className="block text-[11px] text-muted-foreground">{h.details}</span> : null}</span>
    <span className="num text-xs text-muted-foreground">{fmt(h.at)}</span>
  </div>
);

export const CashbookLockReport = ({ refreshKey }) => {
  const [range, setRange] = useState({ start: monthAgo(), end: today() });
  const [d, setD] = useState(null);
  useEffect(() => {
    api.get("/cashbook/lock-report", { params: range }).then((r) => setD(r.data)).catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  }, [range, refreshKey]);

  return (
    <section data-testid="cashbook-lock-report" className="rounded-xl border border-border bg-card p-4 md:p-5 mt-8 space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="font-display text-base md:text-lg font-semibold flex items-center gap-2"><Lock size={16} /> Cash book lock report</h2>
          <p className="text-xs text-muted-foreground">Which days are locked, and who locked or unlocked them and when.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <input data-testid="lock-report-start" type="date" className={ctl} value={range.start} onChange={(e) => setRange({ ...range, start: e.target.value })} />
          <input data-testid="lock-report-end" type="date" className={ctl} value={range.end} onChange={(e) => setRange({ ...range, end: e.target.value })} />
          <button data-testid="lock-report-csv" disabled={!d} onClick={() => exportCsv(d)} className="h-9 px-3 rounded-md border border-input text-sm flex items-center gap-1.5 hover:bg-muted transition-colors"><Download size={14} /> CSV</button>
        </div>
      </div>
      {d && (<>
        <div>
          <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">Locked now ({d.locked.length})</div>
          {d.locked.length === 0 ? <p className="text-sm text-muted-foreground">No locked days in this range.</p> : (
            <div className="flex flex-wrap gap-2">{d.locked.map((l) => (
              <span key={l.date} data-testid={`locked-${l.date}`} title={`${l.by} · ${fmt(l.at)}`} className="text-xs px-2 py-1 rounded-md bg-destructive/10 text-destructive num">{l.date} · {l.by}</span>))}</div>
          )}
        </div>
        <div>
          <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1">Lock / unlock history ({d.history.length})</div>
          {d.history.length === 0 ? <p className="text-sm text-muted-foreground">No lock or unlock in this range.</p>
            : <div className="divide-y divide-border">{d.history.map((h, i) => <HistoryRow key={`${h.at}-${i}`} h={h} i={i} />)}</div>}
        </div>
      </>)}
    </section>
  );
};
